import { call, put, takeEvery, takeLatest } from "redux-saga/effects";

import {
    APPLY_THEME,
    CLOSE_FORM,
    EXECUTE_REACT_CONNECTOR_ACTION,
    HIDE_ALL_FORMS,
    HIDE_FORM_OVERLAY,
    HIDE_LOADING_INDICATOR,
    IApplyThemeAction,
    ICloseFormAction,
    IExecuteReactConnectorActionAction,
    ILoadLocalizationItemsAction,
    ILoadLocalStorageKeyAction,
    ILoadNotificationItemsAction,
    ILoadTaskResultDetailAction,
    IOpenFormAction,
    IOpenFormModalAction,
    IProcessMenuItemCustomActionAction,
    ISetFormAsFavoriteRequestAction,
    ISetGroupNotificationsAsReadAction,
    ISetLocalStorageKeyAction,
    ISetNotificationItemAsReadAction,
    ISetReactConnectorMultiselectModeAction,
    ISwitchToFormAction,
    LOAD_FAVORITE_FORMS,
    LOAD_HISTORY_FORMS_INFO,
    LOAD_LOCAL_STORAGE_KEY,
    LOAD_LOCALIZATION_ITEMS,
    LOAD_MENU_ITEMS,
    LOAD_NOTIFICATION_GROUPS,
    LOAD_NOTIFICATION_ITEMS,
    LOAD_TASK_RESULT_DETAIL,
    LOAD_USER_INFO,
    LOGOUT,
    OPEN_FORM,
    OPEN_FORM_MODAL,
    PROCESS_MENU_ITEM_CUSTOM_ACTION,
    SET_FORM_AS_FAVORITE_REQUEST,
    SET_GROUP_NOTIFICATIONS_AS_READ,
    SET_LOCAL_STORAGE_KEY,
    SET_NOTIFICATION_ITEM_AS_READ,
    SET_REACT_CONNECTOR_MULTISELECT_MODE,
    SHOW_FORM_OVERLAY,
    SHOW_LOADING_INDICATOR,
    SHOW_SETTINGS,
    SWITCH_TO_FORM,
} from "@multiweb/reactCore/bridge/api";
import { SagaWithReturnType, SagaYieldReturn } from "@multiweb/types/react";
import {
    createFavoriteFormsLoadedAction,
    createHistoryFormsInfoUpdatedAction,
    createLocalizationItemsLoadedAction,
    createLocalStorageKeyLoadedAction,
    createMenuItemCustomActionProcessedAction,
    createMenuItemsLoadedAction,
    createNotificationGroupsLoadedAction,
    createNotificationItemsLoadedAction,
    createTaskResultDetailsLoadedAction,
    createUserInfoLoadedAction,
} from "../actions";
import { bridgeInstance } from "./Bridge";

const {
    applyTheme,
    closeForm,
    executeReactConnectorAction,
    getLocalStorageKey,
    getMainMenuItemActionHandler,
    hideAllForms,
    hideFormOverlay,
    hideLoadingIndicator,
    initializeCustomTitleChangedListener,
    initializeFavoriteFormListener,
    initializeFormClosedListener,
    initializeFormItemInstanceCloseRestrictionListener,
    initializeFormOpenedListener,
    initializeFormOverlayClickedListener,
    initializeFormProfileChangedListener,
    initializeFormSwitchedListener,
    initializeHistoryFormsInfoListener,
    initializeNotificationGroupsListener,
    loadFavoriteForms,
    loadHistoryFormsInfo,
    loadLocalizationItems,
    loadMenuItems,
    loadNotificationGroups,
    loadNotificationItems,
    loadTaskResultDetail,
    loadUserInfo,
    logout,
    openForm,
    openFormModal,
    removeCustomTitleChangedListener,
    removeFavoriteFormListener,
    removeFormClosedListener,
    removeFormItemInstanceCloseRestrictionListener,
    removeFormOpenedListener,
    removeFormOverlayClickedListener,
    removeFormProfileChangedListener,
    removeFormSwitchedListener,
    removeHistoryFormsInfoListener,
    removeNotificationGroupsListener,
    setFavoriteForm,
    setLocalStorageKey,
    setNotificationItemAsRead,
    setReactConnectorMultiselectMode,
    showFormOverlay,
    showLoadingIndicator,
    showSettings,
    switchToForm,
    closeAllForms,
    isAnyFormEdited,
    setGroupNotificationsAsRead,
} = bridgeInstance;

/**
 * Hlavní sága pro bridge modul. Zpracovává akce z Reactové části aplikace sloužící pro komunikaci s původní částí aplikace.
 * Na základě toho pak volá odpovídající metody v původní aplikaci. Může také zpracovávat akce vyvolané původní částí aplikace přes
 * volání metod bridge modulu.
 */
export const saga: SagaWithReturnType = function* bridgeSaga() {
    yield takeLatest(LOAD_USER_INFO, loadUserInfoSaga);
    yield takeLatest(LOAD_MENU_ITEMS, loadMenuItemsSaga);
    yield takeEvery(LOAD_LOCALIZATION_ITEMS, loadLocalizationItemsSaga);
    yield takeLatest(SHOW_LOADING_INDICATOR, showLoadingOverlaySaga);
    yield takeLatest(HIDE_LOADING_INDICATOR, hideLoadingOverlaySaga);
    yield takeLatest(OPEN_FORM, openFormSaga);
    yield takeLatest(OPEN_FORM_MODAL, openFormModalSaga);
    yield takeLatest(LOGOUT, logoutSaga);
    yield takeLatest(SHOW_SETTINGS, showSettingsSaga);
    yield takeLatest(SHOW_FORM_OVERLAY, showFormOverlaySaga);
    yield takeLatest(HIDE_FORM_OVERLAY, hideFormOverlaySaga);
    yield takeLatest(LOAD_FAVORITE_FORMS, loadFavoriteFormsSaga);
    yield takeLatest(SET_FORM_AS_FAVORITE_REQUEST, setFormAsFavoriteSaga);
    yield takeLatest(LOAD_NOTIFICATION_ITEMS, loadNotificationItemsSaga);
    yield takeLatest(LOAD_NOTIFICATION_GROUPS, loadNotificationGroupsSaga);
    yield takeLatest(SET_NOTIFICATION_ITEM_AS_READ, setNotificationItemAsReadSaga);
    yield takeLatest(SET_GROUP_NOTIFICATIONS_AS_READ, setGroupNotificationsAsReadSaga);
    yield takeLatest(LOAD_HISTORY_FORMS_INFO, loadHistoryFormsInfoSaga);
    yield takeLatest(CLOSE_FORM, closeFormSaga);
    yield takeLatest(LOAD_TASK_RESULT_DETAIL, loadTaskResultDetailSaga);
    yield takeLatest(HIDE_ALL_FORMS, hideAllFormsSaga);
    yield takeLatest(SWITCH_TO_FORM, switchToFormSaga);
    yield takeLatest(APPLY_THEME, applyThemeSaga);
    yield takeEvery(SET_LOCAL_STORAGE_KEY, setLocalStorageKeySaga);
    yield takeEvery(LOAD_LOCAL_STORAGE_KEY, loadLocalStorageKeySaga);
    yield takeEvery(PROCESS_MENU_ITEM_CUSTOM_ACTION, processMenuItemCustomActionSaga);
    yield takeEvery(EXECUTE_REACT_CONNECTOR_ACTION, executeReactConnectorActionSaga);
    yield takeEvery(SET_REACT_CONNECTOR_MULTISELECT_MODE, setReactConnectorMultiselectModeSaga);
    yield call(initializeFavoriteFormListener);
    yield call(initializeFormProfileChangedListener);
    yield call(initializeHistoryFormsInfoListener);
    yield call(initializeFormOpenedListener);
    yield call(initializeFormClosedListener);
    yield call(initializeFormSwitchedListener);
    yield call(initializeNotificationGroupsListener);
    yield call(initializeCustomTitleChangedListener);
    yield call(initializeFormOverlayClickedListener);
    yield call(initializeFormItemInstanceCloseRestrictionListener);
};

/**
 * Obsluhuje načítání dat o uživateli.
 */
export const loadUserInfoSaga: SagaWithReturnType = function* loadUserInfoSaga() {
    const userInfo: SagaYieldReturn<typeof loadUserInfo> = yield call(loadUserInfo);
    yield put(createUserInfoLoadedAction({
        userId: userInfo.id,
        userName: userInfo.userName,
        displayName: userInfo.fullName,
    }));
};

/**
 * Obsluhuje načítání položek pro menu.
 */
export const loadMenuItemsSaga: SagaWithReturnType = function* loadMenuItemsSaga() {
    const menuItems: SagaYieldReturn<typeof loadMenuItems> = yield call(loadMenuItems);
    yield put(createMenuItemsLoadedAction(menuItems));
};

/**
 * Obsluhuje načítání lokalizačních dat.
 */
export const loadLocalizationItemsSaga: SagaWithReturnType<[ILoadLocalizationItemsAction]> = function* loadLocalizationItemsSaga(action) {
    const { locKeys, key } = action.payload;
    const localizationData: SagaYieldReturn<typeof loadLocalizationItems> = yield call(loadLocalizationItems, locKeys);
    yield put(createLocalizationItemsLoadedAction(localizationData, key));
};

/**
 * Obsluhuje zobrazení načítacího indikátoru.
 */
export const showLoadingOverlaySaga: SagaWithReturnType = function* showLoadingOverlaySaga() {
    yield call(showLoadingIndicator);
};

/**
 * Obsluhuje schování načítacího indikátoru.
 */
export const hideLoadingOverlaySaga: SagaWithReturnType = function* hideLoadingOverlaySaga() {
    yield call(hideLoadingIndicator);
};

/**
 * Obsluhuje otvírání formulářů.
 * @param action
 */
export const openFormSaga: SagaWithReturnType<[IOpenFormAction]> = function* openFormSaga(action) {
    const { formCode, openMethod, openFormData } = action.payload;
    yield call(openForm, formCode, openMethod, openFormData);
};

/**
 * Obsluhuje otvírání modálních formulářů.
 * @param action
 */
export const openFormModalSaga: SagaWithReturnType<[IOpenFormModalAction]> = function* openFormModalSaga(action) {
    const { params } = action.payload;
    yield call(openFormModal, params);
};

/**
 * Obsluhuje odhlášení.
 */
export const logoutSaga: SagaWithReturnType = function* logoutSaga() {
    if (!(yield call(closeAllForms)) && (yield call(isAnyFormEdited))) {
        return;
    }

    yield call(removeCustomTitleChangedListener);
    yield call(removeFavoriteFormListener);
    yield call(removeFormClosedListener);
    yield call(removeFormOpenedListener);
    yield call(removeFormSwitchedListener);
    yield call(removeHistoryFormsInfoListener);
    yield call(removeNotificationGroupsListener);
    yield call(removeFormOverlayClickedListener);
    yield call(removeFormItemInstanceCloseRestrictionListener);
    yield call(removeFormProfileChangedListener);
    yield call(logout);
};

/**
 * Obsluhuje zobrazení nastavení.
 */
export const showSettingsSaga: SagaWithReturnType = function* showSettingsSaga() {
    yield call(showSettings);
};

/**
 * Obsluhuje zobrazení overlaye nad formulářovou částí.
 */
export const showFormOverlaySaga: SagaWithReturnType = function* showFormOverlaySaga() {
    yield call(showFormOverlay);
};

/**
 * Obsluhuje skrytí overlaye nad formulářovou částí.
 */
export const hideFormOverlaySaga: SagaWithReturnType = function* hideFormOverlaySaga() {
    yield call(hideFormOverlay);
};

/**
 * Obsluhuje načítání oblíbených formulářů.
 */
export const loadFavoriteFormsSaga: SagaWithReturnType = function* loadFavoriteFormsSaga() {
    const favoriteFormCodes: SagaYieldReturn<typeof loadFavoriteForms> = yield call(loadFavoriteForms);
    yield put(createFavoriteFormsLoadedAction(favoriteFormCodes));
};

/**
 * Obsluhuje přepínání oblíbených formulářů.
 */
export const setFormAsFavoriteSaga: SagaWithReturnType<[ISetFormAsFavoriteRequestAction]> = function* setFormAsFavoriteSaga(action) {
    const { menuId, formCode, isFavorite, profile } = action.payload;
    yield call(setFavoriteForm, menuId, formCode, isFavorite, profile);
};

/**
 * Obsluhuje označení notifikační položky za přečtenou/nepřečtenou.
 */
export const setNotificationItemAsReadSaga: SagaWithReturnType<[ISetNotificationItemAsReadAction]> = function* setNotificationItemAsReadSaga(action) {
    yield call(setNotificationItemAsRead, action.payload.itemId, action.payload.isRead);
};

/**
 * Obsluhuje označení všech notifikací skupiny za přečtené.
 */
export const setGroupNotificationsAsReadSaga: SagaWithReturnType<[ISetGroupNotificationsAsReadAction]> = function* setGroupNotificationsAsReadSaga(action) {
    yield call(setGroupNotificationsAsRead, action.payload.notificationGroupId);
};

/**
 * Obsluhuje načtení notifikačních položek.
 */
export const loadNotificationItemsSaga: SagaWithReturnType<[ILoadNotificationItemsAction]> = function* loadNotificationItemsSaga(action) {
    const notificationItems: SagaYieldReturn<typeof loadNotificationItems> = yield call(loadNotificationItems, action.payload.notificationGroupId, action.payload.startIndex, action.payload.itemsCount);
    yield put(createNotificationItemsLoadedAction(action.payload.notificationGroupId, notificationItems));
};

/**
 * Obsluhuje načtení notifikačních skupin.
 */
export const loadNotificationGroupsSaga: SagaWithReturnType = function* loadNotificationGroupsSaga() {
    const notificationGroups: SagaYieldReturn<typeof loadNotificationGroups> = yield call(loadNotificationGroups);
    yield put(createNotificationGroupsLoadedAction(notificationGroups));
};

/**
 * Obsluhuje načítání historie otevřených položek.
 */
export const loadHistoryFormsInfoSaga: SagaWithReturnType = function* loadHistoryFormsInfoSaga() {
    const historyInfo: SagaYieldReturn<typeof loadHistoryFormsInfo> = yield call(loadHistoryFormsInfo);
    yield put(createHistoryFormsInfoUpdatedAction(historyInfo));
};

/**
 * Obsluhuje akci pro zavření formuláře.
 */
export const closeFormSaga: SagaWithReturnType<[ICloseFormAction]> = function* closeFormSaga(action) {
    yield call(closeForm, action.payload.formInstanceId);
};

/**
 * Obsluhuje načtení detailu o výsledku úlohy.
 */
export const loadTaskResultDetailSaga: SagaWithReturnType<[ILoadTaskResultDetailAction]> = function* loadTaskResultDetailSaga(action) {
    const taskResultDetail: SagaYieldReturn<typeof loadTaskResultDetail> = yield call(loadTaskResultDetail, action.payload.objId);
    yield put(createTaskResultDetailsLoadedAction(taskResultDetail));
};

/**
 * Obsluhuje skrývání otevřených formulářů.
 */
export const hideAllFormsSaga: SagaWithReturnType = function* hideAllFormsSaga() {
    yield call(hideAllForms);
};

/**
 * Obsluhuje přepnutí otevřeného formuláře do popředí.
 */
export const switchToFormSaga: SagaWithReturnType<[ISwitchToFormAction]> = function* switchToFormSaga(action) {
    yield call(switchToForm, action.payload.formInstanceId);
};

/**
 * Obsluhuje nastavení tématu aplikace.
 */
export const applyThemeSaga: SagaWithReturnType<[IApplyThemeAction]> = function* applyThemeSaga(action) {
    yield call(applyTheme, action.payload.theme);
};

/**
 * Obsluhuje načtení hodnoty pro klíč z local storage.
 */
export const loadLocalStorageKeySaga: SagaWithReturnType<[ILoadLocalStorageKeyAction]> = function* getLocalStorageKeySaga(action) {
    const value: ReturnType<typeof getLocalStorageKey> = yield call(getLocalStorageKey, action.payload.key);
    yield put(createLocalStorageKeyLoadedAction(action.payload.key, value));
};

/**
 * Obsluhuje nastavení hodnoty pro klíč v local storage.
 */
export const setLocalStorageKeySaga: SagaWithReturnType<[ISetLocalStorageKeyAction]> = function* setLocalStorageKeySaga(action) {
    yield call(setLocalStorageKey, action.payload.key, action.payload.value);
};

/**
 * Obsluhuje zpracování custom akce pro položky menu.
 */
export const processMenuItemCustomActionSaga: SagaWithReturnType<[IProcessMenuItemCustomActionAction]> = function* processMenuItemCustomActionSaga(action) {
    let handler: SagaYieldReturn<typeof getMainMenuItemActionHandler>;
    try {
        handler = yield call(getMainMenuItemActionHandler);
    } catch (error) {
        yield put(createMenuItemCustomActionProcessedAction(undefined, {
            handlerNotFound: true,
            errorMessage: error.message,
        }));
        return;
    }
    try {
        const result: SagaYieldReturn<typeof handler.handleAction> = yield call(handler.handleAction, action.payload.menuItemParams);
        yield put(createMenuItemCustomActionProcessedAction(result));
    } catch (error) {
        yield put(createMenuItemCustomActionProcessedAction(undefined, {
            handlerNotFound: false,
            errorMessage: error.message,
        }));
    }
};

/**
 * Zavolá akci connectoru.
 */
export const executeReactConnectorActionSaga: SagaWithReturnType<[IExecuteReactConnectorActionAction]> = function* executeReactConnectorActionSaga(action) {
    const { dataSourceKey, actionId, dataObjectId } = action.payload;
    yield call(executeReactConnectorAction, dataSourceKey, actionId, dataObjectId);
};

/**
 * Nastavuje multiselectMode na connectoru.
 */
export const setReactConnectorMultiselectModeSaga: SagaWithReturnType<[ISetReactConnectorMultiselectModeAction]> = function* setReactConnectorMultiselectModeSaga(action) {
    const { dataSourceKey, multiselectMode } = action.payload;
    yield call(setReactConnectorMultiselectMode, dataSourceKey, multiselectMode);
};
